import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useSearchParams } from "react-router-dom";
import { API, getCached, setCached } from "./cache";
import "./DependenciesView.css";
import EffortEstimatePanel from "./EffortEstimatePanel";
import AttachmentsModal from "./AttachmentsModal";
import { TimesheetEntryModal } from "./DailyTimesheetView";
import { FilterTriggerButton } from "./features/common/components";
import DueDateChangeModal from "./features/common/components/DueDateChangeModal";
import DueDateChangeBadge from "./features/common/components/DueDateChangeBadge";
import "./features/common/styles/PageToolbarSearchFilter.css";

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed", "Escalated", "Cancelled"];
const PRIORITY_OPTIONS = ["Critical","High", "Medium", "Low"];
const DEP_TYPE_OPTIONS = ["API Dependency", "Customer Pre-requisite", "Infra Dependency", "Third Party Dependency", "UI/UX Dependency", "Web Dependency"];

const STATUS_COLORS = {
  "Pending":     { bg: "#F9E8CF", color: "#8F5A12" },
  "In Progress": { bg: "#E7EEF8", color: "#1F4E8F" },
  "Completed":   { bg: "#E8F1E9", color: "#1F6E43" },
  "Escalated":   { bg: "#FDECEC", color: "#B03A2E" },
  "Cancelled":   { bg: "#EFEAE0", color: "#5C6069" },
};

const PRIORITY_COLORS = {
  "Critical": { bg: "#FDECEC", color: "#B03A2E" },
  "High":     { bg: "#FCEFE0", color: "#A25E0A" },
  "Medium":   { bg: "#E9F1FA", color: "#1F4E8F" },
  "Low":      { bg: "#E8F1E9", color: "#1F6E43" },
};

const PRIORITY_ICONS = {
  "Critical":     "🚫",
  "High":         "🔗",
  "Medium":       "🔄",
  "Low":          "ℹ",
};

function isExternalUser(user) {
  const t = String(user?.db_user_type || "").trim().toLowerCase();
  return t === "external" || t === "external user";
}

function fmtDate(v) {
  if (!v) return "—";
  let s = v;
  if (typeof s === "string" && !s.endsWith("Z") && !s.includes("+")) s += "Z";
  const d = new Date(s);
  if (isNaN(d)) return String(v);
  const z = (n) => String(n).padStart(2, "0");
  return `${z(d.getDate())}-${z(d.getMonth() + 1)}-${String(d.getFullYear()).slice(-2)} ${z(d.getHours())}:${z(d.getMinutes())}`;
}

const makeEmptyForm = (taskId = "") => ({
  task_type: "task",
  task_id: taskId,
  requested_to: "",
  dependency_type: "API Dependency",
  title: "",
  description: "",
  priority: "High",
  expected_by: "",
  effort_estimated_hours: 0,
  effort_estimated_minutes: 0,
});

/* ─────────────────────────────────────────────
   User Autocomplete (mirrors MasterView MemberAutocomplete)
   Stores user.id (UUID) instead of userName
───────────────────────────────────────────── */
function UserAutocomplete({ value, users, onChange, placeholder = "Search users…", autoFocus = false }) {
  const toDisplay = (id) => {
    if (!id) return "";
    const u = users.find(u => String(u.id) === String(id));
    return u ? u.displayName : "";
  };

  const [query, setQuery] = useState(() => toDisplay(value ?? ""));
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query) return users;
    const q = query.toLowerCase();
    return users.filter(u => u.displayName.toLowerCase().includes(q));
  }, [query, users]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setQuery(toDisplay(value ?? "")); }, [value, users]);
  useEffect(() => { setActiveIdx(-1); }, [filtered]);
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      listRef.current.children[activeIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const select = (u) => {
    setQuery(u.displayName);
    onChange(String(u.id));
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown")  { e.preventDefault(); setActiveIdx(p => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(p => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) select(filtered[activeIdx]);
      else if (filtered.length === 1) select(filtered[0]);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="dep-autocomplete-wrap" ref={wrapRef}>
      <input
        className="dep-form-input"
        autoFocus={autoFocus}
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(""); }}
        onKeyDown={handleKeyDown}
        onBlur={(e) => { if (wrapRef.current?.contains(e.relatedTarget)) return; setOpen(false); }}
      />
      {open && filtered.length > 0 && (
        <div className="dep-autocomplete-dropdown" ref={listRef}>
          {filtered.map((u, i) => (
            <div
              key={u.id ?? i}
              className={`dep-autocomplete-option${i === activeIdx ? " dep-autocomplete-option--active" : ""}`}
              tabIndex={-1}
              onMouseDown={(e) => { e.preventDefault(); select(u); }}
            >
              {u.displayName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Entity Autocomplete — unified search across
   tasks, features, sub-features, projects, defects
───────────────────────────────────────────── */
const ENTITY_TYPE_COLORS = {
  task:        { bg: "#e8f5e9", color: "#2e7d32" },
  feature:     { bg: "#e3f2fd", color: "#1565c0" },
  sub_feature: { bg: "#f3e5f5", color: "#6a1b9a" },
  project:     { bg: "#e8eaf6", color: "#1a237e" },
  defect:      { bg: "#ffebee", color: "#b71c1c" },
};
const ENTITY_TYPE_LABELS = {
  task: "Task", feature: "Feature", sub_feature: "Sub-Feature",
  project: "Project", defect: "Defect",
};

function EntityAutocomplete({ value, entities, onChange }) {
  const toLabel = (id) => {
    if (!id) return "";
    const e = entities.find(e => e.id === id);
    return e ? e.label : "";
  };

  const [query, setQuery] = useState(() => toLabel(value ?? ""));
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return entities.slice(0, 80);
    const q = query.toLowerCase();
    return entities.filter(e =>
      e.label?.toLowerCase().includes(q) ||
      e.sublabel?.toLowerCase().includes(q) ||
      e.description?.toLowerCase().includes(q)
    ).slice(0, 80);
  }, [query, entities]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setQuery(toLabel(value ?? "")); }, [value, entities]);
  useEffect(() => { setActiveIdx(-1); }, [filtered]);
  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      listRef.current.children[activeIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const select = (e) => {
    setQuery(e.label);
    onChange(e.id, e.task_type);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown")  { e.preventDefault(); setActiveIdx(p => Math.min(p + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx(p => Math.max(p - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && filtered[activeIdx]) select(filtered[activeIdx]);
      else if (filtered.length === 1) select(filtered[0]);
    }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="dep-autocomplete-wrap" ref={wrapRef}>
      <input
        className="dep-form-input"
        value={query}
        placeholder="Search tasks, features, projects, defects…"
        onFocus={() => setOpen(true)}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange("", ""); }}
        onKeyDown={handleKeyDown}
        onBlur={(e) => { if (wrapRef.current?.contains(e.relatedTarget)) return; setOpen(false); }}
      />
      {open && (
        <div className="dep-autocomplete-dropdown dep-autocomplete-dropdown--wide" ref={listRef}>
          {filtered.length === 0 && (
            <div className="dep-autocomplete-empty">No items found</div>
          )}
          {filtered.map((item, i) => {
            const tc = ENTITY_TYPE_COLORS[item.task_type] || { bg: "#f0f0f0", color: "#333" };
            return (
              <div
                key={item.id ?? i}
                className={`dep-autocomplete-option dep-autocomplete-option--entity${i === activeIdx ? " dep-autocomplete-option--active" : ""}`}
                tabIndex={-1}
                onMouseDown={(e) => { e.preventDefault(); select(item); }}
              >
                <span className="dep-entity-badge" style={{ background: tc.bg, color: tc.color }}>
                  {ENTITY_TYPE_LABELS[item.task_type] || item.task_type}
                </span>
                <div className="dep-entity-text">
                  <span className="dep-entity-label">{item.label}</span>
                  {item.sublabel && <span className="dep-entity-sublabel">{item.sublabel}</span>}
                  {item.description && <span className="dep-entity-desc">{item.description}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Add Dependency Panel
───────────────────────────────────────────── */
function AddDepPanel({ form, setForm, entities, users, submitting, onCreate, onClose }) {
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <>
      <div className="dep-panel__header">
        <div>
          <div className="dep-panel__title">➕ Add Dependency</div>
          <div className="dep-panel__subtitle">Create a new dependency request</div>
        </div>
        <button className="dep-panel__close" onClick={onClose}>✕</button>
      </div>

      <div className="dep-panel__body">
        <div className="dep-form-group">
          <label className="dep-form-label">Title *</label>
          <input
            className="dep-form-input"
            value={form.title}
            onChange={e => set("title", e.target.value)}
            placeholder="e.g. API auth must be ready before UI integration"
            autoFocus
          />
        </div>

        <div className="dep-form-group">
          <label className="dep-form-label">Description</label>
          <textarea
            className="dep-form-textarea"
            value={form.description}
            onChange={e => set("description", e.target.value)}
            placeholder="Additional context about this dependency…"
          />
        </div>

        <div className="dep-form-group">
          <label className="dep-form-label">Linked Item</label>
          <EntityAutocomplete
            value={form.task_id}
            entities={entities}
            onChange={(id, taskType) => setForm(f => ({ ...f, task_id: id, task_type: taskType || "task" }))}
          />
        </div>

        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Request From *</label>
            <UserAutocomplete
              value={form.requested_to}
              users={users}
              placeholder="Search users…"
              onChange={id => set("requested_to", id)}
            />
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label">Type</label>
            <select
              className="dep-form-select"
              value={form.dependency_type}
              onChange={e => set("dependency_type", e.target.value)}
            >
              {DEP_TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Priority</label>
            <select
              className="dep-form-select"
              value={form.priority}
              onChange={e => set("priority", e.target.value)}
            >
              {PRIORITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label">Expected By</label>
            <input
              type="datetime-local"
              className="dep-form-input"
              value={form.expected_by}
              onChange={e => set("expected_by", e.target.value)}
            />
          </div>
        </div>
        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Est. Hours <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>(0–160)</span></label>
            <input
              type="number"
              className="dep-form-input"
              min={0}
              max={160}
              step={1}
              value={form.effort_estimated_hours ?? 0}
              onChange={e => set("effort_estimated_hours", Math.min(160, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="0"
            />
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label">Est. Minutes <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>(0–59)</span></label>
            <input
              type="number"
              className="dep-form-input"
              min={0}
              max={59}
              step={1}
              value={form.effort_estimated_minutes ?? 0}
              onChange={e => set("effort_estimated_minutes", Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))}
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div className="dep-panel__footer">
        <button className="dep-btn dep-btn--secondary" onClick={onClose}>Cancel</button>
        <button className="dep-btn dep-btn--primary" onClick={onCreate} disabled={submitting}>
          {submitting ? "Creating…" : "Create Dependency"}
        </button>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   Edit Dependency Panel
───────────────────────────────────────────── */
function EditDepPanel({ dep, form, setForm, entities, users, submitting, onSave, onClose }) {
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <>
      <div className="dep-panel__header">
        <div className="dep-panel__header-text">
          <div className="dep-panel__title">Edit Dependency</div>
          <div className="dep-panel__subtitle" title={dep.title}>{dep.title}</div>
        </div>
        <button className="dep-panel__close" onClick={onClose}>✕</button>
      </div>

      <div className="dep-panel__body">
        <div className="dep-form-group">
          <label className="dep-form-label">Title *</label>
          <input
            className="dep-form-input"
            value={form.title}
            onChange={e => set("title", e.target.value)}
            placeholder="e.g. API auth must be ready before UI integration"
            autoFocus
          />
        </div>

        <div className="dep-form-group">
          <label className="dep-form-label">Description</label>
          <textarea
            className="dep-form-textarea"
            value={form.description ?? ""}
            onChange={e => set("description", e.target.value)}
            placeholder="Additional context about this dependency…"
          />
        </div>

        <div className="dep-form-group">
          <label className="dep-form-label">Linked Item</label>
          <EntityAutocomplete
            value={form.task_id}
            entities={entities}
            onChange={(id, taskType) => setForm(f => ({ ...f, task_id: id, task_type: taskType || "task" }))}
          />
        </div>

        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Requested By</label>
            <UserAutocomplete
              value={form.requested_by}
              users={users}
              placeholder="Search users…"
              onChange={id => set("requested_by", id)}
            />
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label">Requested To</label>
            <UserAutocomplete
              value={form.requested_to}
              users={users}
              placeholder="Search users…"
              onChange={id => set("requested_to", id)}
            />
          </div>
        </div>

        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Type</label>
            <select
              className="dep-form-select"
              value={form.dependency_type}
              onChange={e => set("dependency_type", e.target.value)}
            >
              {DEP_TYPE_OPTIONS.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label">Priority</label>
            <select
              className="dep-form-select"
              value={form.priority}
              onChange={e => set("priority", e.target.value)}
            >
              {PRIORITY_OPTIONS.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div className="dep-form-row">
          <div className="dep-form-group">
            <label className="dep-form-label">Status</label>
            <select
              className="dep-form-select"
              value={form.status}
              onChange={e => set("status", e.target.value)}
            >
              {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="dep-form-group">
            <label className="dep-form-label" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Expected By
              {dep?.id && <DueDateChangeBadge entityType="dependency" entityId={dep.id} />}
            </label>
            <input
              type="datetime-local"
              className="dep-form-input"
              value={form.expected_by ?? ""}
              onChange={e => set("expected_by", e.target.value)}
            />
          </div>
        </div>

        <div className="dep-form-group">
          <label className="dep-form-label">Resolution Note</label>
          <textarea
            className="dep-form-textarea"
            value={form.resolution_note ?? ""}
            onChange={e => set("resolution_note", e.target.value)}
            placeholder="Describe how this was resolved…"
          />
        </div>
      </div>

      <div className="dep-panel__footer">
        <button className="dep-btn dep-btn--secondary" onClick={onClose}>Cancel</button>
        <button className="dep-btn dep-btn--primary" onClick={onSave} disabled={submitting}>
          {submitting ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   Detail Panel
───────────────────────────────────────────── */
const DEP_FIELD_LABELS = {
  title: "Title", description: "Description", dependency_type: "Type",
  priority: "Priority", status: "Status", expected_by: "Expected By",
  resolution_note: "Resolution Note", requested_by: "Requested By",
  requested_to: "Requested To", task_id: "Feature",
};

export function DepDetailPanel({
  user,
  dep, users, featureLabel,
  updateMode, setUpdateMode,
  resolutionNote, setResolutionNote,
  escalateTo, setEscalateTo,
  completedAt, setCompletedAt,
  submitting, onMarkComplete, onEscalate, onDelete, onEdit, onClose,
}) {
  const [activeTab,    setActiveTab]    = useState("overview");
  const [showAttModal, setShowAttModal] = useState(false);
  const [tsEntries,    setTsEntries]    = useState(null);
  const [tsLoading,    setTsLoading]    = useState(false);
  const [showTsModal,  setShowTsModal]  = useState(false);
  const externalUser = isExternalUser(user);

  const loadTimeEntries = useCallback(async () => {
    if (!dep?.id) return;
    setTsLoading(true);
    try {
      const res = await fetch(`${API}/daily-reports?ref_type=dependency&ref_id=${dep.id}`, { credentials: "include" });
      const data = await res.json();
      setTsEntries(Array.isArray(data) ? data : []);
    } catch {
      setTsEntries([]);
    } finally {
      setTsLoading(false);
    }
  }, [dep?.id]);

  useEffect(() => {
    if (activeTab === "effort" && !externalUser) loadTimeEntries();
  }, [activeTab, externalUser, loadTimeEntries]);

  const sc = STATUS_COLORS[dep.status]   || { bg: "#F2F4F7", color: "#667085" };
  const pc = PRIORITY_COLORS[dep.priority] || { bg: "#F2F4F7", color: "#667085" };

  const TABS = [
    { key: "overview",    label: "Overview" },
    { key: "attachments", label: "Attachments" },
    ...(externalUser ? [] : [{ key: "effort", label: "Effort" }]),
  ];

  useEffect(() => {
    if (externalUser && activeTab === "effort") {
      setActiveTab("overview");
    }
  }, [externalUser, activeTab]);

  return (
    <>
      {/* Header */}
      <div className="dep-panel__header">
        <div className="dep-panel__header-info">
          <div className="dep-panel__title">{dep.title}</div>
          <div className="dep-panel__header-badges">
            <span className="dep-badge" style={{ background: sc.bg, color: sc.color }}>{dep.status}</span>
            <span className="dep-badge" style={{ background: pc.bg, color: pc.color }}>
              {PRIORITY_ICONS[dep.priority]} {dep.priority}
            </span>
            <span className="dep-badge dep-badge--type">{dep.dependency_type}</span>
          </div>
        </div>
        <button className="dep-panel__close" onClick={onClose}>✕</button>
      </div>

      {/* Tabs */}
      <div className="dep-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`dep-tab${activeTab === t.key ? " dep-tab--active" : ""}`}
            onClick={() => setActiveTab(t.key)}
            type="button"
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab body */}
      <div className="dep-panel__body">

        {/* Overview tab */}
        {activeTab === "overview" && (
          <>
            <div className="dep-info-grid">
              {[
                { label: "Feature",       value: featureLabel(dep.task_id) },
                { label: "Type",          value: dep.dependency_type },
                { label: "Requested By",  value: dep.requested_by_name },
                { label: "Requested To",  value: dep.requested_to_name },
                { label: "Priority",      value: dep.priority },
                { label: "Status",        value: dep.status },
                { label: "Expected By",   value: fmtDate(dep.expected_by) },
                { label: "Created",       value: fmtDate(dep.created_at) },
                dep.completed_at && { label: "Completed At", value: fmtDate(dep.completed_at) },
                dep.escalated_at && { label: "Escalated At", value: fmtDate(dep.escalated_at) },
              ].filter(Boolean).map(({ label, value }) => (
                <div key={label} className="dep-info-row">
                  <span className="dep-info-label">{label}</span>
                  <span className="dep-info-value">{value || "—"}</span>
                </div>
              ))}
              {dep.description && (
                <div className="dep-info-row dep-info-row--full">
                  <span className="dep-info-label">Description</span>
                  <span className="dep-info-value">{dep.description}</span>
                </div>
              )}
              {dep.resolution_note && (
                <div className="dep-info-row dep-info-row--full">
                  <span className="dep-info-label">Resolution Note</span>
                  <span className="dep-info-value dep-info-value--note">{dep.resolution_note}</span>
                </div>
              )}
              {dep.escalated_to && (
                <div className="dep-info-row dep-info-row--full">
                  <span className="dep-info-label">Escalated To</span>
                  <span className="dep-info-value">
                    {users.find(u => u.id === dep.escalated_to)?.displayName || dep.escalated_to}
                  </span>
                </div>
              )}
            </div>

            {updateMode === "complete" && (
              <div className="dep-update-form">
                <div className="dep-update-form__title">✅ Mark as Completed</div>
                <div className="dep-form-group">
                  <label className="dep-form-label">Resolution Note</label>
                  <textarea
                    className="dep-form-textarea"
                    value={resolutionNote}
                    onChange={e => setResolutionNote(e.target.value)}
                    placeholder="Describe how this was resolved…"
                    autoFocus
                  />
                </div>
                <div className="dep-form-group">
                  <label className="dep-form-label">Completed At</label>
                  <input
                    type="datetime-local"
                    className="dep-form-input"
                    value={completedAt}
                    onChange={e => setCompletedAt(e.target.value)}
                  />
                </div>
                <div className="dep-update-form__btns">
                  <button className="dep-btn dep-btn--success" onClick={() => onMarkComplete(dep)} disabled={submitting}>
                    {submitting ? "Saving…" : "Confirm Complete"}
                  </button>
                  <button className="dep-btn dep-btn--secondary" onClick={() => setUpdateMode(null)}>Cancel</button>
                </div>
              </div>
            )}

            {updateMode === "escalate" && (
              <div className="dep-update-form">
                <div className="dep-update-form__title">⚠️ Escalate</div>
                <div className="dep-form-group">
                  <label className="dep-form-label">Escalate To *</label>
                  <UserAutocomplete
                    value={escalateTo}
                    users={users}
                    placeholder="Search users…"
                    autoFocus
                    onChange={id => setEscalateTo(id)}
                  />
                </div>
                <div className="dep-form-group">
                  <label className="dep-form-label">Reason / Note</label>
                  <textarea
                    className="dep-form-textarea"
                    value={resolutionNote}
                    onChange={e => setResolutionNote(e.target.value)}
                    placeholder="Reason for escalation…"
                  />
                </div>
                <div className="dep-update-form__btns">
                  <button className="dep-btn dep-btn--warning" onClick={() => onEscalate(dep)} disabled={submitting}>
                    {submitting ? "Saving…" : "Confirm Escalate"}
                  </button>
                  <button className="dep-btn dep-btn--secondary" onClick={() => setUpdateMode(null)}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Attachments tab */}
        {activeTab === "attachments" && (
          <div className="dep-attachments">
            <button
              className="dep-btn dep-btn--primary"
              onClick={() => setShowAttModal(true)}
              type="button"
            >
              📎 Manage Attachments
            </button>
          </div>
        )}

        {showAttModal && (
          <AttachmentsModal
            refType="dependency"
            refId={dep.id}
            title="Dependency Attachments"
            defaultCaption="Dependencies"
            lockCaption
            onClose={() => setShowAttModal(false)}
          />
        )}

        {/* Effort tab — estimated effort + actual time log */}
        {activeTab === "effort" && (
          <>
            <EffortEstimatePanel refType="dependency" refId={dep.id} />

            <div className="dep-ts-section">
              <div className="dep-ts-header">
                <span className="dep-ts-title">Time Log</span>
                <button className="dep-ts-add-btn" type="button" onClick={() => setShowTsModal(true)}>
                  + Log Time
                </button>
              </div>
              {tsLoading && <div className="dep-ts-empty">Loading…</div>}
              {!tsLoading && tsEntries !== null && tsEntries.length === 0 && (
                <div className="dep-ts-empty">No time entries yet.</div>
              )}
              {!tsLoading && tsEntries && tsEntries.length > 0 && (
                <table className="dep-ts-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Time</th>
                      <th>Logged By</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tsEntries.map((e) => (
                      <tr key={e.id}>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {e.report_date ? new Date(e.report_date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                        </td>
                        <td>{e.title || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {(e.hours_spent || 0) > 0 || (e.minutes_spent || 0) > 0
                            ? `${e.hours_spent || 0}h ${e.minutes_spent || 0}m`
                            : "—"}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{e.user_display_name || e.user_name || "—"}</td>
                        <td>
                          <span className={`dep-ts-badge dep-ts-badge--${(e.status || "draft").toLowerCase()}`}>
                            {e.status || "Draft"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {showTsModal && dep?.id && createPortal(
              <TimesheetEntryModal
                refType="dependency"
                refId={dep.id}
                onClose={() => setShowTsModal(false)}
                onCreated={loadTimeEntries}
              />,
              document.body
            )}
          </>
        )}
      </div>

      {/* Footer actions (Overview tab only when no update mode active) */}
      {activeTab === "overview" && !updateMode && (
        <div className="dep-panel__footer dep-panel__footer--actions">
          <button className="dep-btn dep-btn--primary dep-btn--outline" onClick={onEdit}>
            ✏️ Edit
          </button>
          <button className="dep-btn dep-btn--danger dep-btn--outline" onClick={() => onDelete(dep.id)}>
            ✕ Delete
          </button>
          {(dep.status === "Pending" || dep.status === "In Progress") && (
            <button className="dep-btn dep-btn--success" onClick={() => setUpdateMode("complete")}>
              ✅ Mark Complete
            </button>
          )}
          {dep.status !== "Escalated" && dep.status !== "Completed" && dep.status !== "Cancelled" && (
            <button className="dep-btn dep-btn--warning" onClick={() => setUpdateMode("escalate")}>
              ⚠️ Escalate
            </button>
          )}
          <span className="dep-footer-spacer" />
          <button className="dep-btn dep-btn--secondary" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────
   Filter Drawer
───────────────────────────────────────────── */
function DepFilterDrawer({ open, statusFilter, onStatusChange, statusCounts, onClose }) {
  if (!open) return null;
  return (
    <>
      <div className="cmn-fdr-scrim" onClick={onClose} />
      <div className="cmn-fdr">
        <div className="cmn-fdr-header">
          <span className="cmn-fdr-title">Filters</span>
          <button className="cmn-fdr-reset" onClick={() => { onStatusChange("All"); onClose(); }}>Reset</button>
        </div>
        <div className="cmn-fdr-list">
          <div className="cmn-fdr-field" style={{ borderBottom: "none" }}>
            <div className="cmn-fdr-field-hdr" style={{ cursor: "default" }}>
              <span className="cmn-fdr-field-name">Status</span>
            </div>
            <div className="cmn-fdr-field-body">
              <div className="cmn-fdr-opts">
                {["All", "Pending", "In Progress", "Completed", "Escalated", "Cancelled"].map(s => (
                  <label key={s} className="cmn-fdr-opt" style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="dep-status-filter"
                      checked={statusFilter === s}
                      onChange={() => { onStatusChange(s); onClose(); }}
                    />
                    {s}
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#8b8f99" }}>
                      {statusCounts[s] ?? 0}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   Main DependenciesView
───────────────────────────────────────────── */
export default function DependenciesView({ showToast, user }) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [deps, setDeps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [users, setUsers] = useState([]);
  const [entities, setEntities] = useState([]);
  const [viewMode, setViewMode] = useState("global"); // "global" | "my"
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null); // dep.id
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [panel, setPanel] = useState(null); // null | { mode: "add" } | { mode: "detail", dep }
  const [form, setForm] = useState(makeEmptyForm());
  const [submitting, setSubmitting] = useState(false);

  const [dueDateModal, setDueDateModal] = useState(null);

  // Detail panel update state
  const [updateMode, setUpdateMode] = useState(null); // "complete" | "escalate" | null
  const [resolutionNote, setResolutionNote] = useState("");
  const [escalateTo, setEscalateTo] = useState("");
  const [completedAt, setCompletedAt] = useState("");

  const actionMenuRef = useRef(null);
  const tableWrapRef  = useRef(null);
  const auditRef      = useRef(null);
  const [auditPopup, setAuditPopup] = useState(null);

  const normalizedUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return users.map((u, i) => {
      const displayName = (
        u?.display_name ?? u?.displayName ?? u?.name ?? u?.full_name ??
        u?.fullName ?? u?.username ?? u?.user_name ?? u?.userName ?? u?.email ?? ""
      ).toString().trim();
      if (!displayName) return null;
      return { ...u, displayName, id: u?.id ?? u?.user_id ?? u?.email ?? i };
    }).filter(Boolean);
  }, [users]);

  const featureLabel = useCallback((taskId) => {
    if (!taskId) return "—";
    const e = entities.find(e => e.id === taskId);
    if (!e) return taskId.slice(0, 8) + "…";
    return e.sublabel ? `${e.label} (${e.sublabel})` : e.label;
  }, [entities]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const cachedUsers = getCached("users-dropdown");
      const validCachedUsers = Array.isArray(cachedUsers) && cachedUsers.length > 0;

      const [data, ud, tasks, features, subFeatures, cp, defects] = await Promise.all([
        fetch(`${API}/dependency-requests`, { credentials: "include" }).then(r => r.json()),
        validCachedUsers
          ? Promise.resolve(cachedUsers)
          : fetch(`${API}/users/dropdown`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/tasks`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/features`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/sub-features`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/customers-projects?active_only=true`, { credentials: "include" }).then(r => r.json()).catch(() => []),
        fetch(`${API}/defects`, { credentials: "include" }).then(r => r.json()).catch(() => []),
      ]);

      if (!validCachedUsers && Array.isArray(ud) && ud.length > 0) setCached("users-dropdown", ud);

      // Build unified entity list
      const unified = [];

      if (Array.isArray(tasks)) {
        tasks.forEach(t => {
          if (!t.id) return;
          unified.push({
            id: t.id,
            task_type: "task",
            label: [t.feature, t.sub_feature].filter(Boolean).join(" — ") || "Untitled",
            sublabel: [t.customer, t.project].filter(Boolean).join(" / "),
            description: t.description || "",
          });
        });
      }

      if (Array.isArray(features)) {
        features.forEach(f => {
          if (!f.id) return;
          unified.push({
            id: f.id,
            task_type: "feature",
            label: f.feature_name || "Unnamed Feature",
            sublabel: f.product_name || "",
          });
        });
      }

      if (Array.isArray(subFeatures)) {
        subFeatures.forEach(sf => {
          if (!sf.id) return;
          unified.push({
            id: sf.id,
            task_type: "sub_feature",
            label: sf.sub_feature_name || "Unnamed Sub-Feature",
            sublabel: sf.sub_feature_code || "",
          });
        });
      }

      if (Array.isArray(cp)) {
        cp.forEach(customer => {
          (customer.projects || []).forEach(p => {
            if (!p.id) return;
            unified.push({
              id: p.id,
              task_type: "project",
              label: p.project_name,
              sublabel: customer.customer_name,
            });
          });
        });
      }

      if (Array.isArray(defects)) {
        defects.forEach(d => {
          if (!d.id) return;
          unified.push({
            id: d.id,
            task_type: "defect",
            label: d.title,
            sublabel: d.code || "",
          });
        });
      }

      setDeps(Array.isArray(data)
        ? data.map(d => ({
            ...d,
            id:         d.id         ?? d["d.id"]         ?? d.dependency_id ?? d.request_id ?? d._id,
            created_at: d.created_at ?? d["d.created_at"],
            updated_at: d.updated_at ?? d["d.updated_at"],
          }))
        : []);
      setUsers(ud);
      setEntities(unified);
    } catch {
      showToast("Cannot reach API", "warn");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-open dependency when ?id= is in the URL (deep-link from DefectsView)
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || !deps.length) return;
    const dep = deps.find(d => d.id === id);
    if (dep) {
      setPanel({ mode: "detail", dep });
      setUpdateMode(null);
      setResolutionNote("");
      setEscalateTo("");
      setCompletedAt("");
      setAuditPopup(null);
      setSearchParams({}, { replace: true });
    }
  }, [deps, searchParams, setSearchParams]);

  // Handle navigation state from MasterView "Add Dependency"
  useEffect(() => {
    if (location.state?.openAdd) {
      setForm(makeEmptyForm(location.state.taskId || ""));
      setPanel({ mode: "add" });
    }
  }, [location.state]);

  // Click-outside to close action menu (skip clicks on the trigger button itself)
  useEffect(() => {
    if (!openActionMenu) return;
    const h = (e) => {
      if (e.target.closest(".action-btn")) return;
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) setOpenActionMenu(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openActionMenu]);

  // Close on table scroll or window resize (fixed menu would drift otherwise)
  useEffect(() => {
    if (!openActionMenu) return;
    const close = () => setOpenActionMenu(null);
    const el = tableWrapRef.current;
    el?.addEventListener("scroll", close);
    window.addEventListener("resize", close);
    return () => { el?.removeEventListener("scroll", close); window.removeEventListener("resize", close); };
  }, [openActionMenu]);

  // Close audit popup on outside click
  useEffect(() => {
    if (!auditPopup) return;
    const h = (e) => { if (auditRef.current && !auditRef.current.contains(e.target)) setAuditPopup(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [auditPopup]);

  // Escape to close panel
  useEffect(() => {
    if (!panel) return;
    const h = (e) => { if (e.key === "Escape") setPanel(null); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [panel]);

  const filtered = useMemo(() => {
    let list = deps;
    if (viewMode === "my") {
      console.log("[MyView] user object:", user);
      console.log("[MyView] sample requested_to_name:", list[0]?.requested_to_name);
      if (user?.name) list = list.filter(d => d.requested_to_name === user.name);
    }
    if (statusFilter !== "All") list = list.filter(d => d.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.title?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        d.requested_by_name?.toLowerCase().includes(q) ||
        d.requested_to_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [deps, statusFilter, search, viewMode, user]);

  const statusCounts = useMemo(() => {
    const c = { All: deps.length };
    STATUS_OPTIONS.forEach(s => { c[s] = deps.filter(d => d.status === s).length; });
    return c;
  }, [deps]);

  const handleActionClick = (e, depId) => {
    if (openActionMenu === depId) { setOpenActionMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
    setOpenActionMenu(depId);
  };

  const openDetail = (dep) => {
    setPanel({ mode: "detail", dep });
    setUpdateMode(null);
    setResolutionNote("");
    setEscalateTo("");
    setCompletedAt("");
    setAuditPopup(null);
  };

  const onShowDepHistory = async (depId, colKey, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popupW = 340;
    const x = rect.left + popupW > window.innerWidth
      ? Math.max(0, rect.right - popupW)
      : rect.left;
    const y = rect.bottom + 4;
    try {
      const res = await fetch(`${API}/audit-logs/dependency_requests/${depId}/${colKey}`, { credentials: "include" });
      const logs = await res.json();
      setAuditPopup({ depId, colKey, logs, pos: { x, y } });
    } catch {
      showToast("Could not load audit history", "warn");
    }
  };

  const openAdd = (taskId = "") => {
    setForm(makeEmptyForm(taskId));
    setPanel({ mode: "add" });
  };

  const openEdit = (dep) => {
    setForm({
      task_type:       dep.task_type       ?? "task",
      task_id:         dep.task_id         ?? "",
      requested_by:    dep.requested_by    ?? "",
      requested_to:    dep.requested_to    ?? "",
      dependency_type: dep.dependency_type ?? "",
      title:           dep.title           ?? "",
      description:     dep.description     ?? "",
      resolution_note: dep.resolution_note ?? "",
      priority:        dep.priority        ?? "High",
      status:          dep.status          ?? "Pending",
      expected_by:     dep.expected_by     ? dep.expected_by.slice(0, 16) : "",
    });
    setPanel({ mode: "edit", dep });
  };

  const doEditDep = async (extraFields = {}) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/dependency-requests/${panel.dep.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, ...extraFields }),
      });
      if (!res.ok) throw new Error();
      const updated = { ...panel.dep, ...form };
      setDeps(prev => prev.map(d => d.id === panel.dep.id ? updated : d));

      // Post audit logs for each changed field
      const auditFields = ["title","description","dependency_type","priority","status",
                           "expected_by","resolution_note","requested_by","requested_to","task_id"];
      auditFields.forEach(key => {
        const oldVal = String(panel.dep[key] ?? "");
        const newVal = String(form[key] ?? "");
        if (oldVal !== newVal) {
          fetch(`${API}/audit-logs`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              table_name: "dependency_requests",
              row_id: panel.dep.id,
              column_key: key,
              old_value: oldVal,
              new_value: newVal,
            }),
          }).catch(() => {});
        }
      });

      showToast("Dependency updated ✓");
      setPanel(null);
    } catch {
      showToast("Failed to update dependency", "warn");
    }
    setSubmitting(false);
  };

  const editDep = async () => {
    if (!form.title.trim()) { showToast("Title is required", "warn"); return; }
    const origExpBy = panel.dep?.expected_by ? String(panel.dep.expected_by).slice(0, 10) : "";
    const newExpBy  = form.expected_by        ? String(form.expected_by).slice(0, 10)       : "";
    if (newExpBy && newExpBy !== origExpBy) {
      setDueDateModal({ fromDate: origExpBy, toDate: newExpBy });
      return;
    }
    await doEditDep();
  };

  const createDep = async () => {
    if (!form.title.trim()) { showToast("Title is required", "warn"); return; }
    if (!form.task_id) { showToast("Please select a feature", "warn"); return; }
    if (!form.requested_to) { showToast("Please select who to request from", "warn"); return; }
    setSubmitting(true);
    try {
      const body = { ...form, ...(user?.id ? { requested_by: user.id } : {}) };
      const res = await fetch(`${API}/dependency-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      showToast("Dependency created ✓");
      setPanel(null);
      fetchAll();
    } catch {
      showToast("Failed to create dependency", "warn");
    }
    setSubmitting(false);
  };

  const updateDep = async (id, fields) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/dependency-requests/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(fields),
      });
      if (!res.ok) throw new Error();
      showToast("Updated ✓");
      setDeps(prev => prev.map(d => d.id === id ? { ...d, ...fields } : d));
      if (panel?.mode === "detail" && panel.dep.id === id) {
        setPanel(p => ({ ...p, dep: { ...p.dep, ...fields } }));
      }
      setUpdateMode(null);
    } catch {
      showToast("Update failed", "warn");
    }
    setSubmitting(false);
  };

  const deleteDep = async (id) => {
    if (!window.confirm("Delete this dependency?")) return;
    try {
      const res = await fetch(`${API}/dependency-requests/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setDeps(prev => prev.filter(d => d.id !== id));
      showToast("Deleted");
      if (panel?.dep?.id === id) setPanel(null);
    } catch {
      showToast("Delete failed", "warn");
    }
  };

  const handleMarkComplete = async (dep) => {
    const now = new Date().toISOString().slice(0, 16);
    await updateDep(dep.id, {
      status: "Completed",
      resolution_note: resolutionNote,
      completed_at: completedAt || now,
    });
  };

  const handleEscalate = async (dep) => {
    if (!escalateTo) { showToast("Please select who to escalate to", "warn"); return; }
    await updateDep(dep.id, {
      status: "Escalated",
      escalated_to: escalateTo,
      escalated_at: new Date().toISOString().slice(0, 16),
      resolution_note: resolutionNote,
    });
  };

  return (
    <>
      <header className="page-head">
        <h1>
          Dependencies{" "}
          <em>{loading ? "loading…" : `${filtered.length} Showing · ${deps.length} Total`}</em>
        </h1>
        <div className="page-head-actions">
          <button className="dep-new-btn v2-btn-primary" onClick={() => openAdd()}>
            + Add Dependency
          </button>
        </div>
      </header>

      <div className="page-toolbar">
        {/* LEFT: view tabs */}
        <div className="page-tabs">
          <button
            className={`page-tab${viewMode === "global" ? " page-tab--active" : ""}`}
            onClick={() => setViewMode("global")}
          >
            Global View
            <span className="page-tab-count">{deps.length}</span>
          </button>
          <button
            className={`page-tab${viewMode === "my" ? " page-tab--active" : ""}`}
            onClick={() => setViewMode("my")}
          >
            My View
          </button>
        </div>

        {/* RIGHT: search + filter */}
        <div className="page-toolbar-right">
          <div className="dep-toolbar-search">
            <svg className="dep-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="dep-search-input"
              placeholder="Search title, members…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <FilterTriggerButton
            activeCount={statusFilter !== "All" ? 1 : 0}
            onClick={() => setFilterDrawerOpen(true)}
            onClear={() => setStatusFilter("All")}
          />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="dep-table-wrap" ref={tableWrapRef}>
        <table className="dep-table">
          <thead>
            <tr>
              <th className="dep-th dep-th--meta"></th>
              <th className="dep-th dep-th--title">Title</th>
              <th className="dep-th">Type</th>
              <th className="dep-th">Priority</th>
              <th className="dep-th">Status</th>
              <th className="dep-th">Requested By</th>
              <th className="dep-th">Requested To</th>
              <th className="dep-th">Expected By</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.map((dep, idx) => {
              const sc = STATUS_COLORS[dep.status] || { bg: "#F2F4F7", color: "#667085" };
              const pc = PRIORITY_COLORS[dep.priority] || { bg: "#F2F4F7", color: "#667085" };
              return (
                <tr
                  key={dep.id}
                  className={`dep-tr${!dep.is_read ? " dep-tr--unread" : ""}`}
                  onClick={() => openDetail(dep)}
                >
                  <td className="dep-td dep-td--meta" onClick={e => e.stopPropagation()}>
                    <div className="dep-meta-inner">
                      <span className="row-num">{idx + 1}</span>
                      <button
                        className="action-btn"
                        title="Actions"
                        onClick={(e) => handleActionClick(e, dep.id)}
                      >
                        ⋯
                      </button>
                    </div>
                  </td>
                  <td className="dep-td dep-td--title">
                    <span className="dep-title-text">{dep.title}</span>
                    {dep.description && (
                      <span className="dep-desc-text">{dep.description}</span>
                    )}
                    <span className="dep-info-icon" title="Title history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "title", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td">
                    <span className="dep-type-badge">{dep.dependency_type}</span>
                    <span className="dep-info-icon" title="Type history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "dependency_type", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td">
                    <span className="dep-badge" style={{ background: pc.bg, color: pc.color }}>
                      {PRIORITY_ICONS[dep.priority]} {dep.priority}
                    </span>
                    <span className="dep-info-icon" title="Priority history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "priority", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td">
                    <span className="dep-badge" style={{ background: sc.bg, color: sc.color }}>
                      {dep.status}
                    </span>
                    <span className="dep-info-icon" title="Status history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "status", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td dep-td--name">
                    {dep.requested_by_name || "—"}
                    <span className="dep-info-icon" title="Requested By history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "requested_by", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td dep-td--name">
                    {dep.requested_to_name || "—"}
                    <span className="dep-info-icon" title="Requested To history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "requested_to", e); }}>ℹ</span>
                  </td>
                  <td className="dep-td dep-td--date">
                    {fmtDate(dep.expected_by)}
                    <span className="dep-info-icon" title="Expected By history" onClick={e => { e.stopPropagation(); onShowDepHistory(dep.id, "expected_by", e); }}>ℹ</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {loading && <div className="state-loading">Loading…</div>}
        {!loading && deps.length === 0 && (
          <div className="state-empty">
            No dependency requests yet. Click "+ Add Dependency" to create one.
          </div>
        )}
        {!loading && deps.length > 0 && filtered.length === 0 && (
          <div className="state-empty">No results. Try adjusting your search or filters.</div>
        )}
      </div>

      {/* ── Fixed-position action menu (renders outside table to avoid overflow clipping) ── */}
      {openActionMenu && (() => {
        const menuDep = filtered.find(d => d.id === openActionMenu);
        if (!menuDep) return null;
        return createPortal(
          <div
            ref={actionMenuRef}
            className="action-menu"
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          >
            <button className="action-menu__item" onClick={() => { openDetail(menuDep); setOpenActionMenu(null); }}>
              👁 View Details
            </button>
            {(menuDep.status === "Pending" || menuDep.status === "In Progress") && (
              <button className="action-menu__item" onClick={() => { openDetail(menuDep); setUpdateMode("complete"); setOpenActionMenu(null); }}>
                ✅ Mark Complete
              </button>
            )}
            {menuDep.status !== "Escalated" && menuDep.status !== "Completed" && menuDep.status !== "Cancelled" && (
              <button className="action-menu__item" onClick={() => { openDetail(menuDep); setUpdateMode("escalate"); setOpenActionMenu(null); }}>
                ⚠️ Escalate
              </button>
            )}
            <div className="action-menu__divider" />
            <button className="action-menu__item action-menu__item--danger" onClick={() => { deleteDep(menuDep.id); setOpenActionMenu(null); }}>
              ✕ Delete
            </button>
          </div>,
          document.body
        );
      })()}

      {/* ── Side Panel (Add / Detail) ── */}
      {panel && (
        <div className="dep-overlay" onClick={() => setPanel(null)}>
          <div className="dep-panel" onClick={e => e.stopPropagation()}>
            {panel.mode === "add" ? (
              <AddDepPanel
                form={form}
                setForm={setForm}
                entities={entities}
                users={normalizedUsers}
                submitting={submitting}
                onCreate={createDep}
                onClose={() => setPanel(null)}
              />
            ) : panel.mode === "edit" ? (
              <EditDepPanel
                dep={panel.dep}
                form={form}
                setForm={setForm}
                entities={entities}
                users={normalizedUsers}
                submitting={submitting}
                onSave={editDep}
                onClose={() => setPanel(null)}
              />
            ) : (
              <DepDetailPanel
                user={user}
                dep={panel.dep}
                users={normalizedUsers}
                featureLabel={featureLabel}
                updateMode={updateMode}
                setUpdateMode={setUpdateMode}
                resolutionNote={resolutionNote}
                setResolutionNote={setResolutionNote}
                escalateTo={escalateTo}
                setEscalateTo={setEscalateTo}
                completedAt={completedAt}
                setCompletedAt={setCompletedAt}
                submitting={submitting}
                onMarkComplete={handleMarkComplete}
                onEscalate={handleEscalate}
                onDelete={deleteDep}
                onEdit={() => openEdit(panel.dep)}
                onClose={() => setPanel(null)}
              />
            )}
          </div>
        </div>
      )}

      {/* ── Audit popup ── */}
      {auditPopup && createPortal(
        <div className="audit-popup audit-popup--dep" ref={auditRef}
          style={{ left: auditPopup.pos.x, top: auditPopup.pos.y }}>
          <div className="audit-popup__header">
            <span className="audit-popup__title">
              {DEP_FIELD_LABELS[auditPopup.colKey] ?? auditPopup.colKey}
            </span>
            <button className="audit-popup__close" onClick={() => setAuditPopup(null)}>✕</button>
          </div>
          <div className="audit-popup__list">
            {auditPopup.logs.length === 0 && (
              <div className="audit-entry audit-entry--empty">No edit history</div>
            )}
            {auditPopup.logs.map((log, i) => {
              let ds = log.changed_at;
              if (typeof ds === "string" && !ds.endsWith("Z") && !ds.includes("+")) ds += "Z";
              const d = new Date(ds);
              const z = n => String(n).padStart(2, "0");
              const ts = `${z(d.getDate())}-${z(d.getMonth()+1)}-${String(d.getFullYear()).slice(-2)} ${z(d.getHours())}:${z(d.getMinutes())}`;
              return (
                <div key={log.id ?? i} className="audit-entry">
                  <span className="audit-entry__time">{ts}</span>
                  <span className="audit-entry__change">{log.old_value || '""'} → {log.new_value || '""'}</span>
                  <span className="audit-entry__user">by {log.changed_by}</span>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      <DepFilterDrawer
        open={filterDrawerOpen}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        statusCounts={statusCounts}
        onClose={() => setFilterDrawerOpen(false)}
      />

      {dueDateModal && createPortal(
        <DueDateChangeModal
          fromDate={dueDateModal.fromDate}
          toDate={dueDateModal.toDate}
          onConfirm={async (reason) => {
            setDueDateModal(null);
            await doEditDep({ due_date_change_reason: reason });
          }}
          onCancel={() => setDueDateModal(null)}
        />,
        document.body
      )}
    </>
  );
}
